import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();

      // QA round 1, F-26 / F-141.
      //
      // Scoped precisely, because two reviewers disagreed and both were right.
      // Opened from the HEADER cart button this drawer is faultless: focus moves
      // onto "Close dialog", it is trapped (0 of 22 Tab presses escaped, at both
      // widths), Escape closes it and focus returns to the trigger.
      //
      // Opened by pressing Add to cart — the path a customer actually takes —
      // the dialog opens as a true modal with the background correctly inert,
      // and document.activeElement stays on <body>. A full-screen panel appears
      // with focus nowhere in it: a screen-reader user gets only the assertive
      // "Added" and has to hunt for the panel, and a sighted keyboard user's
      // focus ring vanishes off the screen.
      //
      // The cause is the section morph that replaces the drawer's DOM after
      // CartAddEvent, so this waits a frame for the new markup before reaching
      // for anything. Only the open side of this one entry point needs it — the
      // dialog component itself is correct and is deliberately left alone.
      requestAnimationFrame(() => {
        const target =
          this.querySelector('[ref="closeButton"], .cart-drawer__close, dialog button') ||
          this.querySelector('dialog');
        if (target instanceof HTMLElement) target.focus();
      });
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
